import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Save, Loader2, Euro } from 'lucide-react';
import { toast } from 'sonner';

import PrimaryButton from '../ui/PrimaryButton';
import { useAuth } from '@/contexts/AuthContext';
import { useCompensations } from '@/contexts/CompensationsContext';
import { monthlyCostFromCompensation } from '@/services/employees/employeeCompensationService';

interface StaffCompensationTabProps {
    data: any; // full hairdresser row (id, shop_id, first_name, ...)
    readOnly?: boolean;
}

const euro = (n: number) => `€ ${Math.round(n).toLocaleString('it-IT')}`;

/**
 * Tab compenso del dipendente (owner-only). Solo la retribuzione:
 * l'analisi delle performance vive nella vista Performance di /staff.
 */
const StaffCompensationTab: React.FC<StaffCompensationTabProps> = ({ data, readOnly = false }) => {
    const { organization } = useAuth();
    const { getByHairdresserId, saveCompensation, loading } = useCompensations();

    const shopId = Number(data?.shop_id ?? organization?.id);
    const hairdresserId = String(data?.id);

    const [isSaving, setIsSaving] = useState(false);

    const [form, setForm] = useState<{ salary_monthly: string; thirteenth: boolean; fourteenth: boolean; tfr: boolean; note: string }>({
        salary_monthly: '',
        thirteenth: false,
        fourteenth: false,
        tfr: false,
        note: '',
    });

    // Compenso del dipendente dal context (già caricato all'apertura della pagina).
    const mine = useMemo(() => getByHairdresserId(hairdresserId), [getByHairdresserId, hairdresserId]);

    useEffect(() => {
        setForm(mine
            ? {
                salary_monthly: mine.salary_monthly != null ? String(mine.salary_monthly) : '',
                thirteenth: !!mine.thirteenth,
                fourteenth: !!mine.fourteenth,
                tfr: !!mine.tfr,
                note: mine.note || '',
            }
            : { salary_monthly: '', thirteenth: false, fourteenth: false, tfr: false, note: '' });
    }, [mine]);

    // Mensilità e costi derivati dal form corrente.
    const mensilita = 12 + (form.thirteenth ? 1 : 0) + (form.fourteenth ? 1 : 0);
    const salaryMonthly = parseFloat(form.salary_monthly) || 0;
    const annualGross = salaryMonthly * mensilita;
    const monthlyCost = monthlyCostFromCompensation({
        salary_monthly: salaryMonthly,
        thirteenth: form.thirteenth,
        fourteenth: form.fourteenth,
        tfr: form.tfr,
    });

    const handleSave = async () => {
        setIsSaving(true);
        const res = await saveCompensation({
            shop_id: shopId,
            hairdresser_id: hairdresserId,
            salary_monthly: salaryMonthly || null,
            salary_annual: annualGross || null,
            thirteenth: form.thirteenth,
            fourteenth: form.fourteenth,
            tfr: form.tfr,
            note: form.note.trim() || null,
        });
        if (res.success) toast.success('Compenso salvato');
        else toast.error(res.error || 'Errore nel salvataggio');
        setIsSaving(false);
    };

    const inputStyle = 'w-full p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 outline-none font-medium disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-100';
    const toggle = (label: string, key: 'thirteenth' | 'fourteenth' | 'tfr', hint: string) => (
        <button
            type="button"
            disabled={readOnly}
            onClick={() => setForm((p) => ({ ...p, [key]: !p[key] }))}
            className={`flex-1 min-w-[120px] px-3 py-2.5 rounded-xl border text-sm font-bold transition-colors text-left disabled:opacity-60 ${form[key] ? 'bg-clip-500 text-white border-clip-500' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}
        >
            <div className="flex items-center justify-between">
                <span>{label}</span>
                <span className={`text-xs ${form[key] ? 'text-white/80' : 'text-gray-400'}`}>{form[key] ? 'sì' : 'no'}</span>
            </div>
            <p className={`text-[11px] font-medium mt-0.5 ${form[key] ? 'text-white/80' : 'text-gray-400'}`}>{hint}</p>
        </button>
    );

    if (loading) {
        return (
            <div className="flex items-center gap-2 text-gray-400 p-10 justify-center">
                <Loader2 size={18} className="animate-spin" /> Carico il compenso…
            </div>
        );
    }

    return (
        <motion.div
            key="compensation"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            className="space-y-4 pt-4 pb-4 px-4 md:px-6"
        >
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2">
                <Euro size={18} className="text-clip-500" /> Compenso
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-1.5">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Stipendio mensile lordo (€)</label>
                    <input
                        type="number"
                        value={form.salary_monthly}
                        onChange={(e) => setForm({ ...form, salary_monthly: e.target.value })}
                        placeholder="es. 1600"
                        className={inputStyle}
                        disabled={readOnly}
                    />
                </div>
                <div className="space-y-1.5">
                    <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Retribuzione annua lorda</label>
                    <div className={`${inputStyle} bg-gray-100 text-gray-700 flex items-center`}>
                        {annualGross > 0 ? `${euro(annualGross)}  ·  ${mensilita} mensilità` : '—'}
                    </div>
                </div>
            </div>

            <div>
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-2 block">Voci aggiuntive</label>
                <div className="flex flex-wrap gap-2">
                    {toggle('Tredicesima', 'thirteenth', '+1 mensilità/anno')}
                    {toggle('Quattordicesima', 'fourteenth', '+1 mensilità/anno')}
                    {toggle('TFR', 'tfr', '~ annua / 13,5')}
                </div>
            </div>

            <div className="space-y-1.5">
                <label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Note (facoltative)</label>
                <input
                    type="text"
                    value={form.note}
                    onChange={(e) => setForm({ ...form, note: e.target.value })}
                    placeholder="es. contratto part-time 30h"
                    className={inputStyle}
                    disabled={readOnly}
                />
            </div>

            <div className="rounded-2xl bg-clip-50 text-clip-700 px-4 py-3 text-sm flex items-center justify-between">
                <span className="font-medium">Costo mensile medio (ratei inclusi)</span>
                <span className="font-bold">{monthlyCost > 0 ? euro(monthlyCost) : '—'}</span>
            </div>

            <p className="text-xs text-gray-400">
                Le performance del dipendente (produzione, servizi, giorni più attivi) sono nella vista <b>Performance</b> della pagina Dipendenti.
            </p>

            {!readOnly && (
                <div className="flex justify-end pt-2 border-t border-gray-100">
                    <PrimaryButton
                        label={isSaving ? 'Salvo…' : 'Salva compenso'}
                        icon={isSaving ? Loader2 : Save}
                        onClick={handleSave}
                        disabled={isSaving}
                    />
                </div>
            )}
        </motion.div>
    );
};

export default StaffCompensationTab;
